import { useCallback, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

/**
 * Click-and-drag horizontal scrolling for an overflowing strip.
 *
 * WHY: MUI's `Tabs variant="scrollable"` gives you a wheel, a trackpad and (on touch) a
 * native swipe — but on a desktop with a mouse there is nothing to grab. The strip looks
 * draggable, is not, and the only way past the overflow is the two chevrons. Reported as
 * "the tabs are not draggable — bad".
 *
 * MOUSE ONLY, on purpose. Touch and pen already scroll natively and far better than
 * anything reimplemented here; hijacking them would replace momentum scrolling with a
 * rigid 1:1 drag and break the swipe users already have.
 *
 * ── 1. A CLICK NEVER ENTERS THE DRAG MACHINERY ────────────────────────────────────
 *
 * The gesture is a two-state machine ({@link advanceDragGesture}): pointerdown only ARMS
 * it, and it ENGAGES on the first move past {@link DRAG_THRESHOLD_PX}. Until it engages
 * nothing is captured, nothing is scrolled and no click is suppressed, so a plain click is
 * exactly the click it would have been without this hook. Deferring engagement is the fix
 * itself, not a refinement:
 *
 *  - The first version called `setPointerCapture` on EVERY pointerdown, before it could
 *    know whether this was a drag. `event.currentTarget` there is MUI's `Tabs` ROOT —
 *    `TabList` spreads unknown props onto `Tabs` (`TabList.js`), which spreads them onto
 *    `TabsRoot` (`Tabs.js`), an ancestor of `.MuiTabs-scroller` → flex container →
 *    `<Tab>` buttons. Pointer capture retargets the ensuing `click` onto the capture
 *    element, and `Tab` selects ONLY from its own `onClick` (`Tab.js`: `handleClick` →
 *    `onChange`; `handleFocus` is inert because `selectionFollowsFocus` is not set). So
 *    the click landed on the Tabs root, `onChange` never fired, and every tab in the CRM
 *    side panel was dead on arrival. `releasePointerCapture` on pointerup does not undo
 *    it — the click target is computed from the already-retargeted down/up pair.
 *  - It read as "side panel only" because of the overflow guard below: the 340–460px
 *    panel always overflows so capture was always taken, while the wide contact page
 *    often fits and returned early. Anyone re-testing this must test in the PANEL.
 *  - Same call, same class of breakage, second time in this repo — `ListingsBoardTable.tsx`
 *    carries the first write-up (capture killed the row's modal-open and the notes icon).
 *    Neither uses capture now.
 *
 * With no capture, the move/up listeners live on `window`. THAT, not capture, is what
 * keeps the drag alive when the pointer leaves a 42px-tall strip.
 *
 * ── 2. ...AND A DRAG NEVER SELECTS THE TAB IT LANDED ON ───────────────────────────
 *
 * The pointerup after a 200px drag lands on a tab and would fire its onClick, so an
 * ENGAGED gesture arms a one-shot swallow, taken in the CAPTURE phase on the host — the
 * only place that beats the target's own handler. Two things stop that flag going stale,
 * and both are load-bearing:
 *
 *  - It is cleared at the TOP of pointerdown, ahead of every guard, so a flag left armed
 *    by a drag released outside the host (no click reaches the host, so the swallower
 *    never runs to clear it) cannot survive into the next mouse click.
 *  - A click with `detail === 0` is never swallowed. Keyboard activation of a native
 *    `<button>` — arrow onto a tab, then Enter/Space — synthesizes a click with no click
 *    count, and it has no preceding pointerdown to clear the flag on its behalf. Without
 *    this, one drag released off the strip would eat the next Enter.
 *
 * ── 3. RTL needs no special case, and that is worth stating ───────────────────────
 *
 * Browsers disagree about the origin of `scrollLeft` under `direction: rtl` (negative in
 * modern Chrome/Firefox, other conventions historically). This never reads an absolute
 * position: it snapshots `scrollLeft` at pointerdown and applies a delta to it, so
 * whatever the origin convention is, the arithmetic is the same. Do not "fix" it by
 * branching on direction.
 *
 * ── 4. Verifying a change here ────────────────────────────────────────────────────
 *
 * The pure half is pinned by `useDragToScroll.gesture.test.ts`. The half that actually
 * broke is DOM-bound and jsdom cannot stand in for it (no pointer capture, no click
 * retargeting), so exercise it in a real browser, in the CRM SIDE PANEL — the wide
 * contact page frequently does not overflow, returns early below, and passes either way:
 *   1. click each tab — it switches and `?tab=` updates;
 *   2. drag the strip ~100px and release over a tab — it scrolls and does NOT switch;
 *   3. keyboard: arrow onto a tab, Enter — it switches;
 *   4. drag off the strip and release over the page, then Enter on a focused tab — it
 *      still switches (this is the stale-flag path §2 closes).
 */

/** Below this, it is still a click. Past it, the gesture engages and the click is eaten. */
export const DRAG_THRESHOLD_PX = 4;

/**
 * A pointerdown that has not yet earned the right to scroll anything.
 *
 * `engaged` is STICKY once true. A drag that swings out 200px and returns to its origin
 * must stay a drag: un-engaging would freeze the scroll position mid-gesture (a visible
 * jump) and then let the release select whatever tab it happened to land on.
 */
export type DragGesture = {
  readonly startX: number;
  readonly startScrollLeft: number;
  readonly engaged: boolean;
};

export type DragGestureStep = {
  readonly gesture: DragGesture;
  /** The `scrollLeft` to write, or `null` while the gesture is still a click. */
  readonly scrollLeft: number | null;
};

export function beginDragGesture(startX: number, startScrollLeft: number): DragGesture {
  return { startX, startScrollLeft, engaged: false };
}

export function advanceDragGesture(gesture: DragGesture, clientX: number): DragGestureStep {
  const dx = clientX - gesture.startX;
  if (!gesture.engaged && Math.abs(dx) <= DRAG_THRESHOLD_PX) return { gesture, scrollLeft: null };
  return {
    gesture: gesture.engaged ? gesture : { ...gesture, engaged: true },
    scrollLeft: gesture.startScrollLeft - dx
  };
}

/**
 * @param armed the gesture that just ended had engaged
 * @param clickDetail `MouseEvent.detail` — 0 for keyboard/programmatic activation, which
 *   is never the tail of a drag and must always reach the tab (see doc block, §2).
 */
export function shouldSwallowClick(armed: boolean, clickDetail: number): boolean {
  return armed && clickDetail > 0;
}

export type DragToScrollHandlers = {
  ref: (node: HTMLElement | null) => void;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
};

/**
 * @param resolveScroller Maps the element the handlers are attached to onto the element
 *   that actually scrolls. Needed because MUI's `Tabs` scrolls an inner
 *   `.MuiTabs-scroller`, not its root — attaching to the root and scrolling the root
 *   would silently do nothing.
 */
export function useDragToScroll(resolveScroller: (host: HTMLElement) => HTMLElement | null): DragToScrollHandlers {
  const hostRef = useRef<HTMLElement | null>(null);
  const gestureRef = useRef<DragGesture | null>(null);
  const swallowNextClickRef = useRef(false);
  // Detaches the window listeners of an in-flight gesture. Held so an unmount mid-drag
  // cannot leave a closure writing `scrollLeft` on a detached scroller.
  const endGestureRef = useRef<(() => void) | null>(null);

  const swallowClickAfterDrag = useCallback((event: MouseEvent) => {
    if (!shouldSwallowClick(swallowNextClickRef.current, event.detail)) return;
    // Cleared here rather than on pointerup: the click arrives AFTER pointerup, so
    // clearing it earlier would let through exactly the click we mean to swallow.
    swallowNextClickRef.current = false;
    event.stopPropagation();
    event.preventDefault();
  }, []);

  // Registered once on the host, in the capture phase, and left there — attaching it only
  // while dragging would mean adding a listener during the very event sequence it has to
  // intercept, which is a race the click can win.
  const ref = useCallback(
    (node: HTMLElement | null) => {
      const previous = hostRef.current;
      if (previous) previous.removeEventListener("click", swallowClickAfterDrag, true);
      endGestureRef.current?.();
      hostRef.current = node;
      if (node) node.addEventListener("click", swallowClickAfterDrag, true);
    },
    [swallowClickAfterDrag]
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      // FIRST, ahead of every guard — see doc block, §2. A guard that returns here must
      // still have cleared a flag a previous gesture left armed.
      swallowNextClickRef.current = false;
      endGestureRef.current?.();

      if (event.pointerType !== "mouse" || event.button !== 0) return;
      const host = hostRef.current;
      const scroller = host && resolveScroller(host);
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

      const { pointerId } = event;
      gestureRef.current = beginDragGesture(event.clientX, scroller.scrollLeft);

      const onMove = (moveEvent: PointerEvent) => {
        const gesture = gestureRef.current;
        if (!gesture || moveEvent.pointerId !== pointerId) return;
        const step = advanceDragGesture(gesture, moveEvent.clientX);
        gestureRef.current = step.gesture;
        if (step.scrollLeft === null) return;
        scroller.scrollLeft = step.scrollLeft;
        // Only once engaged: preventing a pointermove suppresses the compatibility
        // mousemove and with it the text selection that dragging across the Hebrew
        // labels would otherwise start. Doing it before the threshold would change what
        // a plain press does.
        moveEvent.preventDefault();
      };

      const detach = (): void => {
        gestureRef.current = null;
        endGestureRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
      };

      const onUp = (upEvent: PointerEvent) => {
        if (upEvent.pointerId !== pointerId) return;
        swallowNextClickRef.current = gestureRef.current?.engaged === true;
        detach();
      };

      // A cancelled gesture produces no click, so arming the swallow here would only
      // leave a flag for something else to trip over.
      const onCancel = (cancelEvent: PointerEvent) => {
        if (cancelEvent.pointerId !== pointerId) return;
        detach();
      };

      endGestureRef.current = detach;
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
    },
    [resolveScroller]
  );

  return { ref, onPointerDown };
}
